import { batch } from 'react-redux';
import {
  DISABLE_KEYBOARD_SHORTCUTS,
  ENABLE_KEYBOARD_SHORTCUTS,
  REGISTER_KEYBOARD_SHORTCUT,
  UNREGISTER_KEYBOARD_SHORTCUT
} from './actionTypes';
import {
  ACTION_SHORTCUT_PRESSED,
  ACTION_SHORTCUT_RELEASED,
  createShortcutEvent
} from '../analytics/AnalyticsEvents';
import { sendAnalytics } from '../analytics/functions';
import { SETTINGS_TABS } from '../settings/constants';
import { openSettingsDialog } from '../settings/actions';
import logger from './logger';
import { clickOnVideo } from '../filmstrip/actions';
import { areKeyboardShortcutsEnabled, getKeyboardShortcuts } from './functions';
import {
  getClickableElements,
  getKeyboardKey,
  getPriorityFocusedElement
} from './utils';
import { toggleBlurEffect } from '../virtual-background/actions';

/**
 * Action to register a new shortcut.
 *
 * @param {IKeyboardShortcut} shortcut - The shortcut to register.
 * @returns {AnyAction}
 */
export const registerShortcut = (shortcut) => {
  return {
    type: REGISTER_KEYBOARD_SHORTCUT,
    shortcut
  };
};

/**
 * Action to unregister a shortcut.
 *
 * @param {string} character - The character of the shortcut to unregister.
 * @param {boolean} altKey - Whether the shortcut used altKey.
 * @returns {AnyAction}
 */
export const unregisterShortcut = (character, altKey = false) => {
  return {
    alt: altKey,
    type: UNREGISTER_KEYBOARD_SHORTCUT,
    character
  };
};

/**
 * Action to enable keyboard shortcuts.
 *
 * @returns {AnyAction}
 */
export const enableKeyboardShortcuts = () => {
  return {
    type: ENABLE_KEYBOARD_SHORTCUTS
  };
};

/**
 * Action to enable keyboard shortcuts.
 *
 * @returns {AnyAction}
 */
export const disableKeyboardShortcuts = () => {
  return {
    type: DISABLE_KEYBOARD_SHORTCUTS
  };
};

/**
 * Initialise global shortcuts.
 * Global shortcuts are shortcuts for features that don't have a button or
 * link associated with the action. In other words they represent actions
 * triggered _only_ with a shortcut.
 *
 * @returns {Function}
 */
const initGlobalKeyboardShortcuts = () => (dispatch) => {
  batch(() => {
    dispatch(
      registerShortcut({
        character: '?',
        helpDescription: 'keyboardShortcuts.toggleShortcuts',
        handler: () => {
          sendAnalytics(createShortcutEvent('help'));
          dispatch(openSettingsDialog(SETTINGS_TABS.SHORTCUTS, false));
        }
      })
    );

    // register SPACE shortcut in two steps to insure visibility of help message
    dispatch(
      registerShortcut({
        character: ' ',
        helpCharacter: 'SPACE',
        helpDescription: 'keyboardShortcuts.pushToTalk',
        handler: () => {
          sendAnalytics(
            createShortcutEvent('push.to.talk', ACTION_SHORTCUT_RELEASED)
          );
          logger.log('Talk shortcut released');
          // eslint-disable-next-line no-undef
          APP.conference.muteAudio(true);
        }
      })
    );

    dispatch(
      registerShortcut({
        character: '0',
        helpDescription: 'keyboardShortcuts.focusLocal',
        handler: () => {
          dispatch(clickOnVideo(0));
        }
      })
    );
    dispatch(
      registerShortcut({
        character: 'B',
        alt: true,
        helpDescription: 'keyboardShortcuts.blur',
        handler: () => {
          dispatch(toggleBlurEffect(true));
        }
      })
    );
    dispatch(
      registerShortcut({
        character: 'U',
        alt: true,
        helpDescription: 'keyboardShortcuts.unblur',
        handler: () => {
          dispatch(toggleBlurEffect(false));
        }
      })
    );
  });
};

/**
 * Initializes keyboard shortcuts.
 *
 * @returns {Function}
 */
export const initKeyboardShortcuts = () => (dispatch, getState) => {
  dispatch(initGlobalKeyboardShortcuts());

  window.onkeyup = (e) => {
    const state = getState();
    const enabled = areKeyboardShortcutsEnabled(state);
    const shortcuts = getKeyboardShortcuts(state);

    if (!enabled || getPriorityFocusedElement()) {
      return;
    }

    const key = getKeyboardKey(e).toUpperCase();
    if (shortcuts.has(key)) {
      shortcuts.get(key)?.handler(e);
    }
  };

  window.onkeydown = (e) => {
    const state = getState();
    const enabled = areKeyboardShortcutsEnabled(state);

    if (!enabled) {
      return;
    }

    const focusedElement = getPriorityFocusedElement();
    const key = getKeyboardKey(e).toUpperCase();
    const elements = getClickableElements();
    let activeIndex = 0;
    for (let i = 0; i < elements.length; i++) {
      if (document.activeElement === elements[i]) activeIndex = i;
    }
    if (key === ' ' && !focusedElement) {
      sendAnalytics(
        createShortcutEvent('push.to.talk', ACTION_SHORTCUT_PRESSED)
      );
      logger.log('Talk shortcut pressed');
      // eslint-disable-next-line no-undef
      APP.conference.muteAudio(false);
      return;
    }
    switch (key) {
      case 'ESCAPE': {
        focusedElement?.blur();
        break;
      }
      case 'ARROWUP': {
        elements[activeIndex > 0 ? activeIndex - 1 : activeIndex].focus();
        break;
      }
      case 'ARROWLEFT': {
        elements[activeIndex > 0 ? activeIndex - 1 : activeIndex].focus();
        break;
      }
      case 'ARROWRIGHT': {
        elements[activeIndex + 1].focus();
        break;
      }
      case 'ARROWDOWN': {
        elements[activeIndex + 1].focus();
        break;
      }
      case 'ENTER': {
        break;
      }
    }
  };
};

export function focusNextElement() {
  return () => {
    const elements = getClickableElements();
    let activeIndex = 0;
    for (let i = 0; i < elements.length; i++) {
      if (document.activeElement === elements[i]) activeIndex = i;
    }
    elements[activeIndex + 1].focus();
  };
}

export function focusPrevElement() {
  return () => {
    const elements = getClickableElements();
    let activeIndex = 0;
    for (let i = 0; i < elements.length; i++) {
      if (document.activeElement === elements[i]) activeIndex = i;
    }
    elements[activeIndex > 0 ? activeIndex - 1 : activeIndex].focus();
  };
}

export function selectElement() {
  return () => {
    if (document.activeElement) {
      document.activeElement.click();
    }
  };
}
